import React, { useState } from 'react';
import { Eye, EyeOff, Loader, Lock } from 'lucide-react';
import clsx from 'clsx';
import { useChangePassword } from '@api/hooks/useChangePassword';

export const PasswordChangeSection = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationError, setValidationError] = useState('');

  const changePassword = useChangePassword();

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setValidationError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (newPassword.length < 8) {
      setValidationError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('New passwords do not match');
      return;
    }

    changePassword.mutate(
      { current_password: currentPassword, new_password: newPassword },
      { onSuccess: resetForm }
    );
  };

  const inputClasses = clsx(
    'w-full px-3 py-2 pr-10 rounded-lg border',
    'bg-white dark:bg-gray-800',
    'text-gray-900 dark:text-gray-100',
    'border-gray-300 dark:border-gray-700',
    'focus:outline-none focus:ring-2 focus:ring-blue-500',
    'transition-colors duration-150'
  );

  return (
    <div className={clsx(
      'rounded-lg border p-6 mt-6',
      'bg-white dark:bg-gray-900',
      'border-gray-200 dark:border-gray-800',
      'shadow-sm'
    )}>
      <div className="flex items-center gap-2 mb-6">
        <Lock size={18} className="text-gray-500 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Change Password
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {/* Current Password */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Current Password
          </label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className={inputClasses}
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className={inputClasses}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Minimum 8 characters
          </p>
        </div>

        {/* Confirm New Password */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Confirm New Password
          </label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className={inputClasses}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Validation Error */}
        {validationError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {validationError}
          </p>
        )}

        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={changePassword.isPending || !currentPassword || !newPassword || !confirmPassword}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg',
              'bg-blue-600 text-white',
              'hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors duration-150',
              'font-medium'
            )}
          >
            {changePassword.isPending && <Loader size={16} className="animate-spin" />}
            {changePassword.isPending ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
};
